// src/lib/services/publicRecordService.ts

import { DateTime } from "luxon";

// NOTE: Adjust these import paths to your project structure if needed.
import { apiClient } from "@/lib/apiClient";
import { apiPath } from "@/lib/apiPath";
import { getCompanyRegistry } from "@/lib/company/companyRegistrySingleton";
import { getServiceRegistry } from "@/lib/services/serviceRegistrySingleton";

// If you have a central types module for BookingDraft, import it instead.
// This local type is intentionally permissive to avoid project breakage.
export type BookingDraft = {
  // Absolute start in ISO form (e.g., "2025-10-19T08:20:00.000Z").
  startISO: string;

  // Selected master/specialist; "any" means no specific master chosen.
  specialistId?: number | "any" | null;

  // Company context (if required by your API).
  companyId?: number | null;

  // Services chosen by the user; either serviceIds or services may be present.
  serviceIds?: Array<number | string>;
  services?: Array<number | string>;

  // Optional fallbacks from upstream code; used only if registry durations are unavailable.
  totalDurationSeconds?: number | string;
  durationSeconds?: number | string;

  // Contact info collected in the widget.
  contact?: {
    name?: string;
    phone?: string;
    email?: string;
    comment?: string;
  };

  // Confirmation method your API expects (kept from your current flow).
  confirmMethod?: number;

  // Top-level comment (distinct from client comment in some flows).
  comment?: string;
};

// Shape we expect from the service registry.
// Keep it loose so we don't force a specific registry implementation.
type ServiceDescriptor = {
  id: number;
  duration_seconds?: number; // service default in seconds
  staffDurationSeconds?: Record<number, number>; // per-master override in seconds
};

type ServiceRegistry = {
  getServiceById?: (id: number) => ServiceDescriptor | undefined;
  getSnapshot?: () => { services?: Record<number, ServiceDescriptor> };
};

type CompanyRegistry = {
  getTimezone?: () => string | undefined;
};

// What we ultimately POST.
export type PublicRecordPayload = {
  specialistId: number | null;
  staff: null; // preserved from your existing payloads
  startTime: string; // "HH:mm"
  endTime: string; // "HH:mm"
  date: string; // "YYYY-MM-DD"
  company_id: number | null;
  client: {
    name: string;
    phone: string;
    email: string;
    comment: string;
    confirm_method: number;
  };
  client_id: number | null;
  services: number[];
  comment: string;
  isNew: boolean;
};

/**
 * Resolve salon/company timezone; default to UTC to avoid throwing.
 */
function resolveTimezone(): string {
  try {
    const tz = (getCompanyRegistry() as CompanyRegistry | undefined)?.getTimezone?.();
    return tz || "UTC";
  } catch {
    return "UTC";
  }
}

/**
 * Access a service from the registry by id, tolerant of different registry shapes.
 */
function getServiceFromRegistry(reg: ServiceRegistry, id: number): ServiceDescriptor | undefined {
  if (reg.getServiceById) {
    const s = reg.getServiceById(id);
    if (s) return s;
  }
  const snap = reg.getSnapshot?.();
  if (snap?.services && typeof snap.services[id] === "object") {
    return snap.services[id];
  }
  return undefined;
}

/**
 * Compute the effective duration for a service in seconds:
 * - if a positive per-master override exists -> use it
 * - else fall back to the service default
 * - else 0
 */
function getEffectiveServiceDurationSeconds(
  service: ServiceDescriptor | undefined,
  masterId?: number
): number {
  if (!service) return 0;

  const rawCustom =
    masterId && service.staffDurationSeconds ? service.staffDurationSeconds[masterId] : undefined;

  const custom = Number(rawCustom);
  if (Number.isFinite(custom) && custom > 0) return custom;

  const def = Number(service.duration_seconds);
  if (Number.isFinite(def) && def > 0) return def;

  return 0;
}

/**
 * Best-effort parse of number-like values.
 */
function toNumberOrNaN(v: unknown): number {
  const n = typeof v === "string" ? Number(v) : (v as number);
  return Number.isFinite(n) ? n : NaN;
}

/**
 * Build the API payload from a booking draft.
 * This function is exported separately for unit tests.
 */
export function buildPublicRecordPayload(draft: BookingDraft): PublicRecordPayload {
  if (!draft?.startISO) {
    throw new Error("buildPublicRecordPayload: missing draft.startISO");
  }

  const tz = resolveTimezone();
  const reg = getServiceRegistry() as ServiceRegistry;

  const masterId =
    draft?.specialistId && draft.specialistId !== "any"
      ? Number(draft.specialistId)
      : undefined;

  const serviceIdsRaw = (draft.serviceIds ?? draft.services ?? []) as Array<number | string>;
  const serviceIds = serviceIdsRaw.map((s) => Number(s)).filter((n) => Number.isFinite(n));

  // Sum effective per-service duration (override by master when present, else service default).
  let totalDurationSeconds = 0;
  for (const sid of serviceIds) {
    const service = getServiceFromRegistry(reg, sid);
    totalDurationSeconds += getEffectiveServiceDurationSeconds(service, masterId);
  }

  // Fallbacks in case your registry isn't populated in this code path.
  if (!Number.isFinite(totalDurationSeconds) || totalDurationSeconds <= 0) {
    const fallback =
      toNumberOrNaN(draft.totalDurationSeconds) || toNumberOrNaN(draft.durationSeconds);
    if (Number.isFinite(fallback) && fallback > 0) {
      totalDurationSeconds = fallback;
    } else {
      totalDurationSeconds = 0; // will yield endTime === startTime; still valid but not ideal
    }
  }

  // Time math in salon TZ; convert to HH:mm strings for API.
  const start = DateTime.fromISO(draft.startISO).setZone(tz);
  const end = start.plus({ seconds: totalDurationSeconds });

  const payload: PublicRecordPayload = {
    specialistId:
      draft?.specialistId && draft.specialistId !== "any"
        ? Number(draft.specialistId)
        : null,
    staff: null, // keep existing semantics if you already send it
    startTime: start.toFormat("HH:mm"),
    endTime: end.toFormat("HH:mm"), // ✅ respects per-master duration
    date: start.toFormat("yyyy-LL-dd"),
    company_id: draft?.companyId ?? null,
    client: {
      name: draft?.contact?.name ?? "",
      phone: draft?.contact?.phone ?? "",
      email: draft?.contact?.email ?? "",
      comment: draft?.contact?.comment ?? "",
      confirm_method: draft?.confirmMethod ?? 1,
    },
    client_id: null,
    services: serviceIds,
    comment: draft?.comment ?? draft?.contact?.comment ?? "",
    isNew: true,
  };

  return payload;
}

/**
 * Create the public record via API.
 * Keeps the endpoint and client usage consistent with the rest of your codebase.
 */
export async function createPublicRecordFromDraft(draft: BookingDraft) {
  const payload = buildPublicRecordPayload(draft);
  return apiClient.post(apiPath("/record/create"), payload);
}
